import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Button } from '@/ui/rapid-ui'
import type {
	StoreWhatsAppTemplateDataCategoryEnumKey,
	UpdateWhatsAppTemplateParameterMapData,
	WhatsAppTemplateData,
} from '@jobilla/nexus-sdk'
import {
	AlertCircleIcon,
	ArchiveIcon,
	ArchiveRestoreIcon,
	CheckCircleIcon,
	FileTextIcon,
	Loader2Icon,
	PencilIcon,
	PlusIcon,
	RefreshCwIcon,
} from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TemplateCreator } from '../whatsapp-template-creator'
import type { TemplateFormData } from '../whatsapp-template-creator'
import { TemplateAutofillEditor } from './template-autofill-editor'
import { TemplateAutofillManagedNote } from './template-autofill-managed-note'
import { getTemplateBodyText } from './template-body'

const TEMPLATE_CATEGORIES: StoreWhatsAppTemplateDataCategoryEnumKey[] = ['UTILITY', 'MARKETING', 'AUTHENTICATION']

// category is `string` on the API type; fall back to UTILITY for any value the form can't represent.
function toTemplateCategory(value: string): StoreWhatsAppTemplateDataCategoryEnumKey {
	return TEMPLATE_CATEGORIES.includes(value as StoreWhatsAppTemplateDataCategoryEnumKey)
		? (value as StoreWhatsAppTemplateDataCategoryEnumKey)
		: 'UTILITY'
}

export function WhatsAppActiveSection({
	wabaDisplayName,
	scheduledDeactivationDate,
	templates,
	isCreatingTemplate,
	isSyncing,
	isCreating,
	isLoading,
	isDisconnecting,
	isUpdatingParameterMap,
	showArchived,
	isEditing,
	onDisconnect,
	onSyncTemplates,
	onCreateTemplate,
	onSetCreatingTemplate,
	onUpdateParameterMap,
	onSetShowArchived,
	onEditTemplate,
	onArchiveTemplate,
	onUnarchiveTemplate,
}: {
	wabaDisplayName: string | null
	scheduledDeactivationDate: string | null
	templates: WhatsAppTemplateData[]
	isCreatingTemplate: boolean
	isSyncing: boolean
	isCreating: boolean
	isLoading: boolean
	isDisconnecting: boolean
	isUpdatingParameterMap: boolean
	showArchived: boolean
	isEditing: boolean
	onDisconnect: () => void
	onSyncTemplates: () => void
	onCreateTemplate: (data: TemplateFormData) => Promise<void>
	onSetCreatingTemplate: (value: boolean) => void
	onUpdateParameterMap: (templateId: string, data: UpdateWhatsAppTemplateParameterMapData) => Promise<void>
	onSetShowArchived: (value: boolean) => void
	onEditTemplate: (templateId: string, data: TemplateFormData) => Promise<void>
	onArchiveTemplate: (templateId: string) => Promise<void>
	onUnarchiveTemplate: (templateId: string) => Promise<void>
}) {
	const { t } = useTranslation('common')
	const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null)
	const [bodyEditTemplateId, setBodyEditTemplateId] = useState<string | null>(null)
	const [languageFilter, setLanguageFilter] = useState<string>('all')

	const availableLanguages = useMemo(
		() => Array.from(new Set(templates.map((template) => template.language))).sort(),
		[templates],
	)

	// If the templates list changes (sync, create, delete) and the currently
	// selected language no longer exists, fall back to "all" so the user is
	// not stuck looking at an empty filtered list.
	useEffect(() => {
		if (languageFilter !== 'all' && !availableLanguages.includes(languageFilter)) {
			setLanguageFilter('all')
		}
	}, [availableLanguages, languageFilter])

	const groupedTemplates = useMemo(() => {
		const filtered = languageFilter === 'all'
			? templates
			: templates.filter((template) => template.language === languageFilter)

		const byName = new Map<string, WhatsAppTemplateData[]>()
		for (const template of filtered) {
			const list = byName.get(template.name) ?? []
			list.push(template)
			byName.set(template.name, list)
		}

		return Array.from(byName.entries())
			.map(([name, variants]) => ({
				name,
				variants: variants.slice().sort((a, b) => a.language.localeCompare(b.language)),
			}))
			.sort((a, b) => a.name.localeCompare(b.name))
	}, [templates, languageFilter])

	return (
		<>
			{scheduledDeactivationDate && (
				<div
					role="status"
					className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 flex items-center gap-2"
				>
					<AlertCircleIcon className="size-4 flex-shrink-0" />
					{t('whatsappSettings.inactivity.warning', { date: scheduledDeactivationDate })}
				</div>
			)}

			{/* Connected status bar */}
			<div className="bg-green-50 border border-green-200 rounded-xl p-4">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-3">
						<CheckCircleIcon className="size-5 text-green-600" />
						<div>
							<div className="font-medium text-green-900 flex items-center gap-2">
								{t('whatsappSettings.connected')}
								{wabaDisplayName ? `: ${wabaDisplayName}` : ''}
							</div>
							<div className="text-sm text-green-700">
								{t('whatsappSettings.messagesShowCompanyName')}
							</div>
						</div>
					</div>
					<Button type="subtle" onClick={onDisconnect} disabled={isLoading}>
						{isDisconnecting
							? (
								<span className="flex items-center gap-2">
									<Loader2Icon className="size-4 animate-spin" />Disconnecting...
								</span>
							)
							: 'Disconnect'}
					</Button>
				</div>
			</div>

			{/* Templates section */}
			<div className="mt-8 space-y-4">
				<div className="flex items-center justify-between">
					<h3 className="text-lg font-medium text-gray-900">
						{t('whatsappSettings.messageTemplates')}
					</h3>
					<div className="flex items-center gap-2">
						<Button
							type="subtle"
							size="short"
							onClick={() => onSetShowArchived(!showArchived)}
						>
							{showArchived ? t('whatsappSettings.hideArchived') : t('whatsappSettings.showArchived')}
						</Button>
						<Button
							type="subtle"
							size="short"
							onClick={onSyncTemplates}
							disabled={isSyncing}
						>
							<span className="flex items-center gap-1.5">
								<RefreshCwIcon className={`size-4 ${isSyncing ? 'animate-spin' : ''}`} />
								{t('whatsappSettings.sync')}
							</span>
						</Button>
						<Button
							type="neutral"
							size="short"
							onClick={() => onSetCreatingTemplate(true)}
							disabled={isCreatingTemplate}
						>
							<span className="flex items-center gap-1.5">
								<PlusIcon className="size-4" />
								{t('whatsappSettings.create')}
							</span>
						</Button>
					</div>
				</div>

				<p className="text-sm text-gray-500">
					{t('whatsappSettings.templatesDescription')}
				</p>

				<AnimatePresence>
					{isCreatingTemplate && (
						<TemplateCreator
							onSubmit={onCreateTemplate}
							onCancel={() => onSetCreatingTemplate(false)}
							isSubmitting={isCreating}
						/>
					)}
				</AnimatePresence>

				{!isCreatingTemplate && (
					<>
						{availableLanguages.length > 1 && (
							<div className="flex items-center gap-2 mb-3">
								<span className="text-xs text-gray-500 shrink-0">
									{t('whatsappSettings.filterByLanguage')}
								</span>
								<Select value={languageFilter} onValueChange={setLanguageFilter}>
									<SelectTrigger className="w-40 h-8 text-sm">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="all">
											{t('whatsappSettings.allLanguages')}
										</SelectItem>
										{availableLanguages.map((language) => (
											<SelectItem key={language} value={language}>{language}</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						)}

						<div className="border border-gray-200 rounded-xl overflow-hidden">
							{groupedTemplates.length === 0
								? (
									<div className="p-8 text-center">
										<FileTextIcon className="size-10 text-gray-300 mx-auto mb-3" />
										<p className="text-sm text-gray-500">
											{t('whatsappSettings.noTemplates')}
										</p>
									</div>
								)
								: (
									<div className="divide-y divide-gray-100">
										{groupedTemplates.map(({ name, variants }) => {
											const editingVariant = variants.find((variant) =>
												variant.id === editingTemplateId
											)
											const nonUtilityCategory = variants[0].category !== 'UTILITY'
												? variants[0].category
												: null
											return (
												<div key={name} className="px-4 py-3 hover:bg-gray-50/50">
													<div className="flex items-center justify-between gap-3">
														<div className="min-w-0">
															<div className="font-medium text-gray-900 truncate">
																{name}
															</div>
															{nonUtilityCategory && (
																<div className="text-[10px] text-gray-500 uppercase tracking-wide mt-0.5">
																	{nonUtilityCategory}
																</div>
															)}
														</div>
														<div className="flex flex-wrap items-center gap-1.5 justify-end">
															{variants.map((variant) => (
																<TemplateLanguageChip
																	key={variant.id}
																	template={variant}
																	isActive={variant.id === editingTemplateId}
																	onClick={() => {
																		setBodyEditTemplateId(null)
																		setEditingTemplateId(
																			variant.id === editingTemplateId
																				? null
																				: variant.id,
																		)
																	}}
																/>
															))}
														</div>
													</div>

													<AnimatePresence>
														{editingVariant && (
															<motion.div
																initial={{ opacity: 0, height: 0 }}
																animate={{ opacity: 1, height: 'auto' }}
																exit={{ opacity: 0, height: 0 }}
															>
																{editingVariant.isGeneric
																	? (
																		<TemplateAutofillManagedNote
																			template={editingVariant}
																		/>
																	)
																	: editingVariant.archivedAt
																	? (
																		<ArchivedTemplatePanel
																			onUnarchive={() =>
																				void onUnarchiveTemplate(
																					editingVariant.id,
																				)}
																		/>
																	)
																	: bodyEditTemplateId === editingVariant.id
																	? (
																		<div className="mt-3">
																			<TemplateCreator
																				mode="edit"
																				isSubmitting={isEditing}
																				initialValues={{
																					name: editingVariant.name,
																					language: editingVariant.language,
																					category: toTemplateCategory(
																						editingVariant.category,
																					),
																					bodyText: getTemplateBodyText(
																						editingVariant,
																					),
																					exampleParameters: editingVariant
																						.parameterExamples ?? [],
																				}}
																				onSubmit={async (data) => {
																					try {
																						await onEditTemplate(
																							editingVariant.id,
																							data,
																						)
																						setBodyEditTemplateId(null)
																					} catch {
																						// error surfaced by the view model; keep the edit form open
																					}
																				}}
																				onCancel={() =>
																					setBodyEditTemplateId(null)}
																			/>
																		</div>
																	)
																	: (
																		<>
																			<TemplateActionBar
																				isPending={editingVariant.status
																					=== 'PENDING'}
																				onEdit={() =>
																					setBodyEditTemplateId(
																						editingVariant.id,
																					)}
																				onArchive={() =>
																					void onArchiveTemplate(
																						editingVariant.id,
																					)}
																			/>
																			<TemplateAutofillEditor
																				template={editingVariant}
																				isSaving={isUpdatingParameterMap}
																				onSave={async (map) => {
																					await onUpdateParameterMap(
																						editingVariant.id,
																						{
																							parameterMap: map,
																						},
																					)
																					setEditingTemplateId(null)
																				}}
																				onCancel={() =>
																					setEditingTemplateId(null)}
																			/>
																		</>
																	)}
															</motion.div>
														)}
													</AnimatePresence>
												</div>
											)
										})}
									</div>
								)}
						</div>
					</>
				)}
			</div>
		</>
	)
}

function TemplateLanguageChip({
	template,
	isActive,
	onClick,
}: {
	template: WhatsAppTemplateData
	isActive: boolean
	onClick: () => void
}) {
	const { t } = useTranslation('common')
	const status = template.status
	const isArchived = Boolean(template.archivedAt)
	const statusClass = isArchived
		? 'bg-gray-100 text-gray-400 border-gray-200 hover:bg-gray-200'
		: status === 'APPROVED'
		? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
		: status === 'PENDING'
		? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
		: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'

	const mapped = template.parameterMap
		? Object.values(template.parameterMap).filter(
			(value): value is string => typeof value === 'string' && value.trim() !== '',
		).length
		: 0
	const total = template.parameterCount
	const autofillLabel = !template.isGeneric && !isArchived && total > 0 ? ` · ${mapped}/${total}` : ''

	const ringClass = isActive ? 'ring-2 ring-offset-1 ring-purple-300' : ''

	return (
		<button
			type="button"
			onClick={onClick}
			className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${statusClass} ${ringClass}`}
		>
			{template.language}
			{autofillLabel}
			{isArchived && ` · ${t('whatsappSettings.archivedLabel')}`}
		</button>
	)
}

function TemplateActionBar({
	isPending,
	onEdit,
	onArchive,
}: {
	isPending: boolean
	onEdit: () => void
	onArchive: () => void
}) {
	const { t } = useTranslation('common')
	return (
		<div className="mt-3 flex items-center gap-2">
			<span title={isPending ? t('whatsappSettings.editPendingDisabled') : undefined}>
				<Button type="subtle" size="short" onClick={onEdit} disabled={isPending}>
					<span className="flex items-center gap-1.5">
						<PencilIcon className="size-3.5" />
						{t('whatsappSettings.editMessage')}
					</span>
				</Button>
			</span>
			<Button type="subtle" size="short" onClick={onArchive}>
				<span className="flex items-center gap-1.5">
					<ArchiveIcon className="size-3.5" />
					{t('whatsappSettings.archive')}
				</span>
			</Button>
		</div>
	)
}

function ArchivedTemplatePanel({ onUnarchive }: { onUnarchive: () => void }) {
	const { t } = useTranslation('common')
	return (
		<div className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
			<span className="text-sm text-gray-500">{t('whatsappSettings.archivedLabel')}</span>
			<Button type="subtle" size="short" onClick={onUnarchive}>
				<span className="flex items-center gap-1.5">
					<ArchiveRestoreIcon className="size-3.5" />
					{t('whatsappSettings.unarchive')}
				</span>
			</Button>
		</div>
	)
}
